/**
 * Supabase Data Mapper Module
 * Maps between database entities and application types
 */

import {
  DatabaseUser,
  DatabaseClient,
  DatabaseService,
  DatabaseJob,
  DatabaseJobService,
  DatabaseQuote,
  DatabaseQuoteItem,
  DatabasePaymentMethod,
  DatabaseExpense,
  DatabaseCalendarEvent
} from '@/shared/services/types';
import {
  Client,
  Job,
  Service,
  Quote,
  QuoteItem,
  PaymentMethod,
  Expense,
  CalendarEvent,
  UserProfile
} from '@/shared/types';
import { TemplateId, CurrencyCode, UserRole } from '@/shared/types/enums';

/**
 * Map database user to application profile
 */
export function mapDatabaseUserToProfile(dbUser: DatabaseUser): UserProfile {
  return {
    id: dbUser.id,
    userId: dbUser.id, // Alias for id for compatibility with service layer
    name: dbUser.name,
    email: dbUser.email,
    role: (dbUser.role === 'admin' ? UserRole.ADMIN : dbUser.role === 'client' ? UserRole.CLIENT : UserRole.FREELANCER),
    businessName: dbUser.business_name || '',
    businessAddress: dbUser.business_address || '',
    businessEmail: dbUser.business_email || '',
    businessPhone: dbUser.business_phone || '',
    logoUrl: dbUser.logo_url || '',
    brandColor: dbUser.brand_color || '#3B82F6',
    template: dbUser.template as TemplateId,
    currency: dbUser.currency as CurrencyCode,
    country: dbUser.country || 'US',
    subscription: {
      planId: dbUser.subscription_plan_id as 'pro' | 'free' | 'basic' | 'enterprise' | 'company',
      status: dbUser.subscription_status as 'active' | 'canceled' | 'past_due',
      nextBillingDate: dbUser.next_billing_date || new Date().toISOString(),
      billingPeriod: dbUser.billing_period as 'monthly' | 'quarterly' | 'yearly' | 'custom'
    },
    savedServices: [],
    paymentMethods: [],
    phone: dbUser.phone || '',
    joinedAt: dbUser.joined_at,
    hasCompletedOnboarding: dbUser.has_completed_onboarding,
    notificationPreferences: dbUser.notification_preferences || {
      email: { jobUpdates: true, paymentReminders: true, clientOnboarding: true },
      push: { jobUpdates: true, paymentReminders: true, clientOnboarding: false },
      sms: { jobUpdates: false, paymentReminders: false, clientOnboarding: false },
      whatsapp: { jobUpdates: false, paymentReminders: true, clientOnboarding: false }
    }
  };
}

/**
 * Map database client to application client
 */
export function mapDatabaseClientToClient(dbClient: any): Client {
  return {
    id: dbClient.id,
    userId: dbClient.user_id,
    name: dbClient.name,
    email: dbClient.email,
    phone: dbClient.phone,
    company: dbClient.company || '',
    address: dbClient.address || '',
    taxId: dbClient.tax_id || '',
    logoUrl: dbClient.logo_url || '',
    notes: dbClient.notes || '',
    // Edit tracking fields
    editCount: dbClient.edit_count || 0,
    createdAt: dbClient.created_at,
    lastEditedAt: dbClient.last_edited_at || dbClient.updated_at
  };
}

/**
 * Map application client to database client
 */
export function mapClientToDatabaseClient(client: Partial<Client>): Partial<any> {
  const dbClient: any = {
    name: client.name || '',
    email: client.email || '',
    phone: client.phone || '',
    company: client.company || '',
    address: client.address || '',
    tax_id: client.taxId || '',
    logo_url: client.logoUrl || '',
    notes: client.notes || ''
  };

  // Map edit count if present
  if (client.editCount !== undefined) {
    dbClient.edit_count = client.editCount;
  }

  // Map last edited at if present
  if (client.lastEditedAt !== undefined) {
    dbClient.last_edited_at = client.lastEditedAt;
  }

  return dbClient;
}

/**
 * Map database service to application service
 */
export function mapDatabaseServiceToService(dbService: DatabaseService): Service {
  return {
    id: dbService.id,
    userId: dbService.user_id,
    name: dbService.name,
    rate: dbService.rate,
    description: dbService.description || '',
    unit: dbService.unit as 'hourly' | 'fixed' | 'daily' | undefined,
    category: dbService.category,
    createdAt: dbService.created_at,
    updatedAt: dbService.updated_at
  };
}

/**
 * Map application service to database service
 */
export function mapServiceToDatabaseService(service: Partial<Service>): Partial<DatabaseService> {
  return {
    name: service.name || '',
    rate: service.rate || 0,
    description: service.description || '',
    unit: service.unit || undefined,
    category: service.category || undefined
  };
}

/**
 * Map database job to application job
 */
export function mapDatabaseJobToJob(dbJob: any, client: Client, services: Service[]): Job {
  // Calculate totalAmount from services if total_amount is not available
  const totalAmount = dbJob.total_amount || services.reduce((sum, service) => sum + (service.rate || 0), 0);

  return {
    id: dbJob.id,
    userId: dbJob.user_id,
    clientId: dbJob.client_id,
    title: dbJob.title,
    status: dbJob.status as any,
    totalAmount: totalAmount,
    amountPaid: dbJob.amount_paid || 0,
    dueDate: dbJob.due_date,
    publicToken: dbJob.public_token || '',
    createdAt: dbJob.created_at,
    updatedAt: dbJob.updated_at || dbJob.created_at,
    services
  };
}

/**
 * Map database job service to application service with quantity
 */
export function mapDatabaseJobService(dbJobService: DatabaseJobService, service: Service): Service & { quantity: number } {
  return {
    ...service,
    quantity: dbJobService.quantity,
    rate: dbJobService.rate
  };
}

/**
 * Map database quote to application quote
 */
export function mapDatabaseQuoteToQuote(dbQuote: DatabaseQuote, client: Client, items: QuoteItem[]): Quote {
  return {
    id: dbQuote.id,
    userId: dbQuote.user_id,
    clientId: dbQuote.client_id,
    title: dbQuote.title,
    description: dbQuote.description || '',
    status: dbQuote.status as 'draft' | 'sent' | 'accepted' | 'rejected',
    currency: dbQuote.currency as CurrencyCode || 'USD',
    subtotal: dbQuote.subtotal || dbQuote.total_amount,
    taxRate: dbQuote.tax_rate || 0,
    taxAmount: dbQuote.tax_amount || 0,
    totalAmount: dbQuote.total_amount,
    validUntil: dbQuote.valid_until,
    notes: dbQuote.notes || '',
    terms: dbQuote.terms || '',
    createdAt: dbQuote.created_at,
    updatedAt: dbQuote.updated_at,
    publicToken: dbQuote.public_token || '',
    publicShareToken: dbQuote.public_share_token || '',
    quoteNumber: dbQuote.quote_number || '',
    items,
    // Professional quote fields
    projectName: dbQuote.project_name,
    detailLevel: dbQuote.detail_level as 'basic' | 'short' | 'medium' | 'long' | undefined,
    introText: dbQuote.intro_text,
    problemStatement: dbQuote.problem_statement,
    proposedSolution: dbQuote.proposed_solution,
    deliverables: dbQuote.deliverables,
    timeline: dbQuote.timeline,
    startDate: dbQuote.start_date,
    endDate: dbQuote.end_date,
    paymentSchedule: dbQuote.payment_schedule,
    methodology: dbQuote.methodology,
    team: dbQuote.team,
    includesCaseStudy: dbQuote.includes_case_study,
    caseStudyTitle: dbQuote.case_study_title,
    caseStudyDescription: dbQuote.case_study_description
  };
}

/**
 * Map database quote item to application quote item
 */
export function mapDatabaseQuoteItemToQuoteItem(dbQuoteItem: DatabaseQuoteItem): QuoteItem {
  return {
    id: dbQuoteItem.id,
    serviceId: dbQuoteItem.service_id || '',
    description: dbQuoteItem.description,
    rate: dbQuoteItem.rate,
    quantity: dbQuoteItem.quantity
  };
}

/**
 * Map database payment method to application payment method
 */
export function mapDatabasePaymentMethodToPaymentMethod(dbPaymentMethod: DatabasePaymentMethod): PaymentMethod {
  return {
    id: dbPaymentMethod.id,
    name: dbPaymentMethod.name,
    type: dbPaymentMethod.type as 'bank' | 'paypal' | 'mpesa' | 'other',
    details: dbPaymentMethod.details,
    isDefault: dbPaymentMethod.is_default
  };
}

/**
 * Map database expense to application expense
 */
export function mapDatabaseExpenseToExpense(dbExpense: DatabaseExpense): Expense {
  const expense: Expense = {
    id: dbExpense.id,
    category: dbExpense.category as any,
    description: dbExpense.description,
    amount: dbExpense.amount,
    date: dbExpense.date,
    receiptUrl: dbExpense.receipt_url || '',
    notes: dbExpense.notes || '',
    createdAt: dbExpense.created_at,
    currency: dbExpense.currency as any,
    isBillable: dbExpense.is_billable || false
  };

  // Only add updatedAt if it exists in the database
  if (dbExpense.updated_at !== undefined) {
    expense.updatedAt = dbExpense.updated_at;
  }

  // Only add clientId if it exists (exactOptionalPropertyTypes: true)
  if (dbExpense.client_id) {
    expense.clientId = dbExpense.client_id;
  }

  return expense;
}

/**
 * Map database calendar event to application calendar event
 */
export function mapDatabaseCalendarEventToCalendarEvent(dbCalendarEvent: DatabaseCalendarEvent): CalendarEvent {
  return {
    id: dbCalendarEvent.id,
    userId: dbCalendarEvent.user_id,
    title: dbCalendarEvent.title,
    description: dbCalendarEvent.description || '',
    date: dbCalendarEvent.date,
    startTime: dbCalendarEvent.start_time,
    endTime: dbCalendarEvent.end_time,
    color: (dbCalendarEvent.color as 'indigo' | 'green' | 'blue' | 'red' | 'amber' | 'purple') || 'blue',
    type: dbCalendarEvent.type as any
  };
}

/**
 * Map application calendar event to database calendar event
 */
export function mapCalendarEventToDatabaseCalendarEvent(calendarEvent: Partial<CalendarEvent>): Partial<DatabaseCalendarEvent> {
  const dbCalendarEvent: Partial<DatabaseCalendarEvent> = {
    title: calendarEvent.title,
    description: calendarEvent.description || '',
    date: calendarEvent.date || '',
    start_time: calendarEvent.startTime || '',
    end_time: calendarEvent.endTime || '',
    color: calendarEvent.color || 'blue',
    type: calendarEvent.type || 'other'
  };

  return dbCalendarEvent;
}